import { tryParseJSONObject } from '@fox/atoms';
import { IWorkBenchLayout } from '@fox/blocks';
import { ConditionResult } from '../blocks/form-block/conditionals/contracts';
import { renderConditionalCheck } from '../blocks/form-block/conditionals/helpers';
import { FORM_PAGE_VALIDATORS, FormPageValidatorFunctionResult } from '../blocks/form-block/validators/form-page-validators';
import { ISection } from '../blocks/section-block/contracts';
import { FormErrorContextType } from '../form-error-context/form-error-context';
import { FormEventAPIHelpers } from '../form-event-context/form-event-context';
import { FormRenderMode } from '../form-runtime-context/contracts';
import { IField, IQuestionAnswer } from './contracts';

/**
 * Validates form fields on the client side and updates errors in the form error context.
 * Used for validating each page on navigation (e.g., next button) and before final submission.
 *
 * - Evaluates conditional logic for each field.
 * - Runs the appropriate validator for each field type.
 * - Collects and updates validation errors using setErrors.
 * - Navigates to the relevant section if a field fails validation.
 * - Returns true if all fields are valid, false otherwise.
 *
 * @param formRequestId   The unique ID for the form request, used for API calls.
 * @param ownerId         The owner ID for context-specific validation.
 * @param fieldsToValidate The fields to be validated on the current page.
 * @param sections        All form sections.
 * @param answers         Current form answers.
 * @param tempData        Temporary data (e.g., for payment or ticket blocks).
 * @param goToSection     Callback to navigate to a specific section by index.
 * @param formEventApiHelpers  API helpers for ticket/event operations.
 * @param setErrors       Function to update form errors in context.
 * @param isFinalPage     Whether this is the final page of the form.
 * @param layout          (Optional) The full form layout, required for some validations.
 * @returns               Promise<boolean> indicating if all fields are valid (true) or not (false).
 */
export const validateFormClientSide = async (
	formRequestId: string,
	ownerId: string | undefined,
	fieldsToValidate: IField[],
	sections: ISection[],
	answers: IQuestionAnswer[],
	tempData,
	goToSection: (nextSection: number) => void,
	formEventApiHelpers: Pick<FormEventAPIHelpers, 'createTicketReservation' | 'extendReservation' | 'findReservationById' | 'getTicketDetails'>,
	setErrors?: FormErrorContextType['setErrors'],
	isFinalPage?: boolean,
	/** HACK: Sections is missing cruicial data from the layout, so we need to ALSO pass this */
	layout?: IWorkBenchLayout,
	onWaitList?: boolean
): Promise<Boolean> => {
	const validations: FormPageValidatorFunctionResult[] = [];
	const updatedErrors = {};
	const errorsToDelete: string[] = [];
	for (const field of fieldsToValidate) {
		if (FORM_PAGE_VALIDATORS[field.type]) {
			const meta = tryParseJSONObject<any>(field.metadataJson, {});
			const conditionValid = renderConditionalCheck(sections, meta.conditionalDefinitions, answers, FormRenderMode.runtime, field.name, layout);
			if (conditionValid === ConditionResult.Valid) {
				const validationResult = await FORM_PAGE_VALIDATORS[field.type]({
					formRequestId,
					field,
					tempData,
					sections,
					answers,
					isFinalPage: isFinalPage ?? false,
					ownerId,
					layout,
					formEventApiHelpers,
					onWaitList
				});
				const pageNo = sections.findIndex(s => s.fields.find(f => f.name === field.name));
				if (validationResult.success) {
					errorsToDelete.push(field.name);
				} else {
					updatedErrors[field.name] = {
						message: validationResult.errorMessage,
						setPage: () => goToSection?.(pageNo)
					};
				}
				validations.push(validationResult);
			} else {
				// Clear any other fields here as they don't have per page validation
				errorsToDelete.push(field.name);
			}
		} else {
			errorsToDelete.push(field.name);
		}
	}
	setErrors?.(prevErrors => {
		const updated = { ...prevErrors, ...updatedErrors };
		for (const error of errorsToDelete) {
			delete updated[error];
		}
		return updated;
	});
	const valid = validations.every(r => r.success);
	return valid;
};
